import React from 'react';
import { matchPath, RouteComponentProps, RouteProps } from 'react-router-dom';
import Overrides from 'Routes/Overrides';
import MerchantOverride from 'Routes/Overrides/routes/MerchantOverrides';
import MerchantDeployed from 'Routes/Overrides/routes/MerchantOverrides/routes/MerchantDeployed';
import MerchantBulkUpload from 'Routes/Overrides/routes/BulkUploadPage';
import GlobalRulesPage from 'Routes/GlobalRulesPage';
import DeployedGlobalRulesPage from 'Routes/GlobalRulesPage/routes/DeployedGlobalRulesPage';
import GlobalRulesBulkUpload from 'Routes/GlobalRulesPage/routes/BulkUploadPage';
import Revisions from 'Routes/Revisions';
import Reports from 'Routes/Reports';
import { RoleService } from 'Services';
import {
    shouldShowLists,
    shouldShowBulkUploads,
    shouldShowGlobalRules,
    shouldShowMerchants,
    shouldShowSimulations,
    shouldShowReports,
    shouldShowRevisions,
    shouldShowCreateRule,
    shouldShowManageTenants,
    shouldShowAdvancedRules,
    shouldShowParameters,
    shouldShowRuleTester,
    shouldRuleGroupConditions,
    shouldShowManageVelocities,
    shouldShowRuleTests
} from 'Selectors';
import NewReport from 'Routes/Reports/routes/NewReport';
import SavedReport from 'Routes/Reports/routes/SavedReport';
import RevisionWindow from 'Routes/Revisions/routes/RevisionWindow';
import ScheduledRevisionWindow from 'Routes/Revisions/routes/RevisionWindow/ScheduledRevisionWindow';
import ListManagement from 'Routes/ListManagement';
import ListRoute from 'Routes/ListManagement/routes/ListRoute';
import ListEntryBulkUpload from 'Routes/ListManagement/routes/ListEntryBulkUpload';
import ListEntriesImport from 'Routes/ListManagement/routes/ListEntriesImport';
import ViewListEntriesRemovedPage from 'Routes/ListManagement/routes/ViewListEntriesRemovedPage';
import UpdateTemplatedRulePage from 'Routes/GlobalRulesPage/routes/UpdateTemplatedRulePage';
import AddOrEditMerchantOverride from 'Routes/Overrides/routes/AddOrEditMerchantOverride';
import Simulations from 'Routes/Simulations';
import ViewSimulation from 'Routes/Simulations/routes/ViewSimulation';
import UpdateSimpleRulePage from 'Routes/GlobalRulesPage/routes/UpdateSimpleRulePage';
import CreateSimpleRulePage from 'Routes/GlobalRulesPage/routes/CreateSimpleRulePage';
import CreateAdvancedRulePage from 'Routes/GlobalRulesPage/routes/CreateAdvancedRulePage';
import UpdateAdvancedRulePage from 'Routes/GlobalRulesPage/routes/UpdateAdvancedRulePage';
import CreateRuleGroupPage from 'Routes/GlobalRulesPage/routes/CreateRuleGroupPage';
import UpdateRuleGroupPage from 'Routes/GlobalRulesPage/routes/UpdateRuleGroupPage';
import _ from 'lodash';
import ManageTenants from 'Routes/ManageTenants';
import TenantGroup from 'Routes/ManageTenants/routes/TenantGroup';
import TenantGroupDeployed from 'Routes/ManageTenants/routes/TenantGroupDeployed';
import TenantGroupOverride from 'Routes/ManageTenants/routes/TenantGroup/routes/TenantGroupOverride';
import AddTenant from 'Routes/ManageTenants/routes/TenantGroup/routes/AddTenant';
import Parameters from 'Routes/Parameters';
import CreateExpression from 'Routes/Parameters/Pages/CreateExpression';
import EditExpression from 'Routes/Parameters/Pages/EditExpression';
import ViewExpression from 'Routes/Parameters/Pages/ViewExpression';
import CreateVelocity from 'Routes/Parameters/Pages/CreateVelocity';
import RuleTesterLayout from 'Routes/RuleTester/RuleTesterLayout';
import EditVelocity from 'Routes/Parameters/Pages/EditVelocity';
import RuleTests from 'Routes/RuleTests';
import { ROUTES } from './routes';

type FlatRouteArrayItem = {
    isForbidden?: boolean;
    showFeature?: boolean;
} & Pick<RouteProps, 'path' | 'component'>;

export const ROUTES_TO_HIDE_NAV = [ROUTES.SINGLE_REVISION_WINDOW, ROUTES.SCHEDULED_REVISION_WINDOW];

export const RoutesTree = (roleService: RulesRoleService, landlordId?: string, tenantKey?: string): RoutesTreeNode => {
    const reportsIsForbidden = !roleService.hasReportAccess;
    const designIsForbidden = !roleService.hasDesignAccess;
    const bulkUploadOrEditRuleIsForbidden = !roleService.hasEditRuleOrBulkUploadAccess;
    const listIsForbidden = !roleService.hasListOrDesignAccess;
    const addListEntriesIsForbidden = !roleService.hasListAccess;
    const viewRemovedListEntriesIsForbidden = !roleService.hasViewRemovedListEntriesAccess;
    const createRuleIsForbidden = !roleService.hasCreateRuleAccess;
    const updateRuleIsForbidden = !roleService.hasRulesUpdateAccess;
    const editTenantGroupOverridesForbidden = !roleService.hasTenantGroupWriteAccess;
    const parameterIsForbidden = !roleService.hasParameterAccess;
    const tenantGroupIsForbidden = !roleService.hasAnyTenantRole;
    const ruleTesterIsForbidden = !roleService.hasViewRuleTestRole;

    return {
        path: '/',
        subNodes: [
            {
                path: ROUTES.GLOBAL_RULES.INDEX,
                component: GlobalRulesPage,
                breadcrumbTitle: 'Manage Rules',
                isForbidden: designIsForbidden,
                showFeature: shouldShowGlobalRules(),
                subNodes: [
                    {
                        path: ROUTES.GLOBAL_RULES.DEPLOYED_LANDLORD_OVERRIDE,
                        component: DeployedGlobalRulesPage,
                        breadcrumbTitle: 'Add Override'
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.DEPLOYED_LANDLORD,
                        component: DeployedGlobalRulesPage,
                        subNodes: [
                            {
                                path: ROUTES.OVERRIDES_TENANT_RULES_EDIT_OVERRIDE,
                                component: () => <AddOrEditMerchantOverride actionType='edit' overrideLevel='tenant' />,
                                breadcrumbTitle: 'editTenantOverrideBreadcrumb',
                                hasAdditionalParams: true,
                                isForbidden: bulkUploadOrEditRuleIsForbidden
                            },
                            {
                                path: ROUTES.GLOBAL_RULES.DEPLOYED_LANDLORD_OVERRIDE_ADD,
                                component: DeployedGlobalRulesPage,
                                breadcrumbTitle: 'Add Override',
                                subNodes: [
                                    {
                                        path: ROUTES.OVERRIDES_TENANT_RULES_ADD_OVERRIDE,
                                        component: () => (
                                            <AddOrEditMerchantOverride actionType='add' overrideLevel='tenant' />
                                        ),
                                        breadcrumbTitle: 'addTenantOverrideBreadcrumb',
                                        hasAdditionalParams: true,
                                        isForbidden: bulkUploadOrEditRuleIsForbidden
                                    }
                                ]
                            }
                        ]
                    },

                    {
                        path: ROUTES.GLOBAL_RULES.DEPLOYED_TENANT,
                        component: DeployedGlobalRulesPage
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.BULK_UPLOAD,
                        component: GlobalRulesBulkUpload,
                        breadcrumbTitle: 'Bulk Upload',
                        showFeature: shouldShowBulkUploads(),
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.UPDATE_DEFAULT_RULE_PAGE,
                        component: UpdateTemplatedRulePage,
                        breadcrumbTitle: 'editDefaultRuleBreadcrumb',
                        hasAdditionalParams: true,
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.CREATE_SIMPLE_RULE_PAGE,
                        component: CreateSimpleRulePage,
                        breadcrumbTitle: 'New Simple Rule',
                        showFeature: shouldShowCreateRule(),
                        isForbidden: createRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.CREATE_ADVANCED_RULE_PAGE,
                        component: CreateAdvancedRulePage,
                        breadcrumbTitle: 'New Advanced Rule',
                        showFeature: shouldShowAdvancedRules(),
                        isForbidden: createRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.UPDATE_ADVANCED_RULE_PAGE,
                        component: UpdateAdvancedRulePage,
                        breadcrumbTitle: 'editAdvancedRuleBreadcrumb',
                        showFeature: shouldShowAdvancedRules(),
                        isForbidden: updateRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.UPDATE_SIMPLE_RULE_PAGE,
                        component: UpdateSimpleRulePage,
                        breadcrumbTitle: 'editSimpleRuleBreadcrumb',
                        hasAdditionalParams: true,
                        showFeature: shouldShowCreateRule(),
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.CREATE_RULE_GROUP_CONDITIONS,
                        breadcrumbTitle: 'ruleGroupConditions.breadcrumb.create',
                        component: CreateRuleGroupPage,
                        showFeature: shouldRuleGroupConditions()
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.UPDATE_RULE_GROUP_CONDITIONS,
                        breadcrumbTitle: 'ruleGroupConditions.breadcrumb.update',
                        component: UpdateRuleGroupPage,
                        showFeature: shouldRuleGroupConditions()
                    },
                    {
                        path: ROUTES.GLOBAL_RULES.VIEW_RULE_TEST,
                        component: RuleTesterLayout,
                        breadcrumbTitle: 'viewRuleTest',
                        hasAdditionalParams: true,
                        showFeature: shouldShowRuleTester(),
                        isForbidden: ruleTesterIsForbidden
                    }
                ]
            },
            {
                path: ROUTES.TENANTS.INDEX,
                component: ManageTenants,
                breadcrumbTitle: 'Manage Tenants',
                isForbidden: tenantGroupIsForbidden && !shouldShowManageTenants(landlordId, tenantKey),
                showFeature: shouldShowManageTenants(landlordId, tenantKey),
                subNodes: [
                    {
                        path: ROUTES.TENANTS.TENANT_GROUP,
                        component: TenantGroup,
                        breadcrumbTitle: 'tenantGroupOverrideBreadcrumb',
                        hasAdditionalParams: true,
                        params: { tenantGroupName: '' },
                        subNodes: [
                            {
                                path: ROUTES.TENANTS.TENANT_GROUP_ADD_TENANT,
                                component: AddTenant,
                                breadcrumbTitle: 'Add Tenants To Group',
                                hasAdditionalParams: true
                            },
                            {
                                path: ROUTES.TENANTS.TENANT_GROUP_ADD_OVERRIDE,
                                component: () => (
                                    <AddOrEditMerchantOverride actionType='add' overrideLevel='tenantGroup' />
                                ),
                                breadcrumbTitle: 'addTenantGroupOverrideBreadcrumb',
                                hasAdditionalParams: true,
                                isForbidden: editTenantGroupOverridesForbidden
                            },
                            {
                                path: ROUTES.TENANTS.TENANT_GROUP_EDIT_OVERRIDE,
                                component: () => (
                                    <AddOrEditMerchantOverride actionType='edit' overrideLevel='tenantGroup' />
                                ),
                                breadcrumbTitle: 'editTenantGroupOverrideBreadcrumb',
                                hasAdditionalParams: true,
                                isForbidden: editTenantGroupOverridesForbidden
                            },
                            {
                                path: ROUTES.TENANTS.TENANT_GROUP_OVERRIDES_LANDLORD_RULES,
                                component: TenantGroupOverride,
                                breadcrumbTitle: 'New Override',
                                hasAdditionalParams: true
                            }
                        ]
                    },
                    {
                        path: ROUTES.TENANTS.TENANT_GROUP_DEPLOYED,
                        component: TenantGroupDeployed,
                        breadcrumbTitle: 'tenantGroupOverrideBreadcrumb',
                        hasAdditionalParams: true,
                        params: { tenantGroupName: '' }
                    },

                    {
                        path: ROUTES.TENANTS.IMPERSONATE_TENANT,
                        component: ManageTenants,
                        hasAdditionalParams: true,
                        // initialize params to empty so it doesn't  show parameter names
                        params: {
                            list: ''
                        }
                    }
                ]
            },
            {
                path: ROUTES.PARAMETERS.INDEX,
                component: Parameters,
                breadcrumbTitle: 'Manage Parameters',
                isForbidden: parameterIsForbidden,
                showFeature: shouldShowParameters(),
                subNodes: [
                    {
                        path: ROUTES.PARAMETERS.CREATE_NEW_EXPRESSION_PAGE,
                        component: CreateExpression,
                        breadcrumbTitle: 'New Expression',
                        hasAdditionalParams: true,
                        isForbidden: !roleService.isParameterAdmin
                    },
                    {
                        path: ROUTES.PARAMETERS.EDIT_EXPRESSION,
                        component: EditExpression,
                        breadcrumbTitle: 'EditExpressionBreadCrumb',
                        hasAdditionalParams: true,
                        params: { parameterName: '' },
                        isForbidden: !roleService.isParameterAdmin
                    },
                    {
                        path: ROUTES.PARAMETERS.VIEW_EXPRESSION,
                        component: ViewExpression,
                        breadcrumbTitle: 'ViewExpressionBreadCrumb',
                        hasAdditionalParams: true,
                        params: { parameterName: '' },
                        isForbidden: parameterIsForbidden
                    },
                    {
                        path: ROUTES.PARAMETERS.CREATE_NEW_VELOCITY_PAGE,
                        component: CreateVelocity,
                        breadcrumbTitle: 'New Velocity',
                        hasAdditionalParams: true,
                        isForbidden: !roleService.isParameterAdmin,
                        showFeature: shouldShowManageVelocities()
                    },
                    {
                        path: ROUTES.PARAMETERS.EDIT_VELOCITY,
                        component: EditVelocity,
                        breadcrumbTitle: 'EditVelocityBreadCrumb',
                        hasAdditionalParams: true,
                        isForbidden: !roleService.isParameterAdmin,
                        showFeature: shouldShowManageVelocities()
                    }
                ]
            },
            {
                path: ROUTES.OVERRIDES.INDEX,
                component: Overrides,
                breadcrumbTitle: 'Manage Merchants',
                isForbidden: designIsForbidden,
                showFeature: shouldShowMerchants(),
                subNodes: [
                    {
                        path: ROUTES.OVERRIDES_MERCHANT.MID,
                        component: MerchantOverride,
                        breadcrumbTitle: 'merchantOverrideBreadcrumb',
                        subNodes: [
                            {
                                path: ROUTES.OVERRIDES_MERCHANT.MID_DEPLOYED,
                                component: MerchantDeployed
                            }
                        ]
                    },
                    {
                        path: ROUTES.OVERRIDES.BULK_UPLOAD,
                        component: MerchantBulkUpload,
                        breadcrumbTitle: 'Merchant Bulk Upload',
                        showFeature: shouldShowBulkUploads(),
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    },
                    {
                        path: ROUTES.OVERRIDES_MERCHANT.RULES_ADD_OVERRIDE,
                        component: () => <AddOrEditMerchantOverride actionType='add' overrideLevel='merchant' />,
                        breadcrumbTitle: 'addOverrideBreadcrumb',
                        hasAdditionalParams: true,
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    },
                    {
                        path: ROUTES.OVERRIDES_MERCHANT.RULES_EDIT_OVERRIDE,
                        component: () => <AddOrEditMerchantOverride actionType='edit' overrideLevel='merchant' />,
                        breadcrumbTitle: 'editOverrideBreadcrumb',
                        hasAdditionalParams: true,
                        isForbidden: bulkUploadOrEditRuleIsForbidden
                    }
                ]
            },
            {
                path: ROUTES.SIMULATIONS.INDEX,
                component: Simulations,
                isForbidden: designIsForbidden,
                breadcrumbTitle: 'Manage Simulations',
                showFeature: shouldShowSimulations(),
                subNodes: [
                    {
                        path: ROUTES.SIMULATIONS.SIMULATION,
                        component: ViewSimulation,
                        breadcrumbTitle: 'View Simulation'
                    }
                ]
            },
            {
                path: ROUTES.REPORTS,
                component: Reports,
                breadcrumbTitle: 'Manage Reports',
                isForbidden: reportsIsForbidden,
                showFeature: shouldShowReports(),
                subNodes: [
                    {
                        path: ROUTES.NEW_REPORT.MERCHANT_SUMMARY,
                        component: () => <NewReport reportType='merchantsummary' />,
                        breadcrumbTitle: shouldShowMerchants()
                            ? 'New Merchant Summary Report'
                            : 'New Transaction Summary Report'
                    },
                    {
                        path: ROUTES.NEW_REPORT.RULE_PERFORMANCE,
                        component: () => <NewReport reportType='performance' />,
                        breadcrumbTitle: 'New Rule Performance Report'
                    },
                    {
                        path: ROUTES.SAVED_REPORT.MERCHANT_SUMMARY,
                        component: () => <SavedReport reportType='merchantsummary' />,
                        breadcrumbTitle: 'reportBreadcrumb',
                        hasAdditionalParams: true,
                        // initialize params to empty so it doesn't  show parameter names
                        params: {
                            Merchant: '',
                            dateRangeLabel: ''
                        }
                    },
                    {
                        path: ROUTES.SAVED_REPORT.RULE_PERFORMANCE,
                        component: () => <SavedReport reportType='performance' />,
                        breadcrumbTitle: 'reportBreadcrumb',
                        hasAdditionalParams: true,
                        // initialize params to empty so it doesn't  show parameter names
                        params: {
                            Merchant: '',
                            dateRangeLabel: ''
                        }
                    }
                ]
            },
            {
                path: ROUTES.REVISIONS,
                component: Revisions,
                isForbidden: designIsForbidden,
                breadcrumbTitle: 'Manage Revisions',
                showFeature: shouldShowRevisions(),
                subNodes: [
                    {
                        path: ROUTES.SCHEDULED_REVISION_WINDOW,
                        component: ScheduledRevisionWindow,
                        isForbidden: designIsForbidden
                    },
                    {
                        path: ROUTES.SINGLE_REVISION_WINDOW,
                        component: RevisionWindow,
                        isForbidden: designIsForbidden
                    }
                ]
            },
            {
                path: ROUTES.LIST_MANAGEMENT.INDEX,
                component: ListManagement,
                isForbidden: listIsForbidden,
                breadcrumbTitle: 'Manage Lists',
                showFeature: shouldShowLists(),
                subNodes: [
                    {
                        path: ROUTES.LIST_MANAGEMENT.LIST,
                        component: ListRoute,
                        isForbidden: listIsForbidden,
                        breadcrumbTitle: 'listNameBreadcrumb',
                        hasAdditionalParams: true,
                        subNodes: [
                            {
                                path: ROUTES.LIST_MANAGEMENT.BULK_UPLOAD,
                                breadcrumbTitle: 'Bulk Upload Entries',
                                component: ListEntryBulkUpload,
                                isForbidden: addListEntriesIsForbidden
                            },
                            {
                                path: ROUTES.LIST_MANAGEMENT.IMPORT_ENTRIES,
                                breadcrumbTitle: 'Import entries from CSV',
                                component: ListEntriesImport,
                                isForbidden: addListEntriesIsForbidden
                            },
                            {
                                path: ROUTES.LIST_MANAGEMENT.REMOVED_LIST_ENTRIES,
                                breadcrumbTitle: 'Removed Entries',
                                component: ViewListEntriesRemovedPage,
                                isForbidden: viewRemovedListEntriesIsForbidden
                            }
                        ]
                    }
                ]
            },
            {
                path: ROUTES.RULE_TESTS.INDEX,
                component: RuleTests,
                breadcrumbTitle: 'Manage Rule Tests',
                showFeature: shouldShowRuleTests()
            }
        ]
    };
};

export const getBreadcrumbs = (
    location: RouteComponentProps['location'],
    iterationCount = 1,
    node: RoutesTreeNode = RoutesTree(RoleService),
    accumulator: BreadcrumbWrapper = { breadcrumbs: [] }
): BreadcrumbWrapper | null => {
    const { pathname } = location;
    const pathnameLength = pathname.split('/')?.length;
    const nodePathLength = node.path.split('/')?.length;
    // truncate pathname to the parts relevant to the current node.path value
    const truncatedPathname = pathname.split('/').splice(0, nodePathLength).join('/');

    // flag that allows the search to end if the depth of the tree is the same as the number of elements
    // in the current pathname's split array
    const isLastPathnameIteration = pathnameLength === iterationCount;
    const pathMatched = matchPath(pathname, {
        path: node.path,
        exact: isLastPathnameIteration
    });

    if (!pathMatched) {
        return null;
    }
    const copiedAccumulator: BreadcrumbWrapper = {
        ...accumulator
    };
    const { params } = pathMatched;

    if (node.breadcrumbTitle) {
        copiedAccumulator.breadcrumbs.push({
            title: node.breadcrumbTitle,
            hasAdditionalParams: !!node.hasAdditionalParams,
            providedPath: node.path,
            actualPath: truncatedPathname,
            params: { ...params, ...node?.params }
        });
    }
    if (!isLastPathnameIteration) {
        if (node.subNodes) {
            for (let i = 0; i < node.subNodes.length; i++) {
                const subNode = node.subNodes[i];
                const subResults = getBreadcrumbs(location, iterationCount + 1, subNode, copiedAccumulator);
                if (subResults) {
                    return subResults;
                }
            }
        }
    }

    return copiedAccumulator;
};

const recursiveFlatRouteHelper = (
    acc: FlatRouteArrayItem[],
    node: RoutesTreeNode,
    parentNode?: RoutesTreeNode
): FlatRouteArrayItem[] => {
    const clonedNode = _.cloneDeep(node);
    if (node.component) {
        // clone the node so that we aren't messing with the actual defined routes
        // and then mutate the isForbidden for this node to be based on either it's own setting
        // or default to whatever the parent is set to.
        clonedNode.isForbidden = Boolean(clonedNode?.isForbidden ?? parentNode?.isForbidden);
        clonedNode.showFeature = Boolean(clonedNode?.showFeature ?? parentNode?.showFeature);

        acc.push({
            path: clonedNode.path,
            component: clonedNode.component as React.FC,
            isForbidden: clonedNode.isForbidden,
            showFeature: clonedNode.showFeature
        });
    }
    if (!clonedNode.subNodes || clonedNode?.subNodes?.length === 0) return acc;
    return clonedNode.subNodes.reduce(
        (thisAcc: FlatRouteArrayItem[], thisNode: RoutesTreeNode) =>
            recursiveFlatRouteHelper(thisAcc, thisNode, clonedNode),
        acc
    );
};

export const getFlatRoutesArray = (landlordId?: string, tenantKey?: string): FlatRouteArrayItem[] => {
    const node: RoutesTreeNode = RoutesTree(RoleService, landlordId, tenantKey);
    return recursiveFlatRouteHelper([], node);
};
